import { Component, ElementRef, Inject, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { MatDialogRef } from '@angular/material/dialog';
import { DialogComponentContent } from '@toro/common/shared/models/dialogs/DialogComponentContent';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActionEventsEnum } from '@toro/common/shared/enum/tracking/action-events.enum';
import { TrackingActions } from '@toro/common/shared/state-management/tracking/tracking.action';
import { SantanderSuitabilityService } from '@toro/common/shared/services/santander-suitability/santander-suitability-service';

interface DialogMessage {
  title: string;
  message: string;
}

interface Messages {
  PreAnticipation: {
    Varejo: DialogMessage;
    Trader: DialogMessage;
  };
  PostAnticipation?: {
    Varejo: DialogMessage;
    Trader: DialogMessage;
  };
}

@AutoUnsubscribe()
@Component({
  selector: 'suitability-santander',
  templateUrl: './suitability-anticipation-dialog.component.html',
  styleUrls: ['./suitability-anticipation-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SuitabilityAnticipationDialogComponent extends DialogComponentContent {

  public content: DialogMessage;
  public profileType: string;
  public isPreAnticipation = false;
  public isTrader = false;
  public isExclusive = false;
  public productAreaOrigin;

  private messages: Messages = {
    PreAnticipation: {
      Varejo: {
        title: 'Novas regras do Perfil de Investidor',
        message: `<span class="content-text"><span class="text-highlight">A partir de [00] de [mês]</span>, as regras de Perfil de Investidor serão ajustadas para deixar sua experiência
        mais personalizada.</span>
        <span>Para evitar interrupções futuras, responda às perguntas agora, e seu perfil será
        atualizado na data da mudança.</span>`
      },
      Trader: {
        title: 'Atualize seu perfil para continuar operando Day Trade',
        message: `<span class="content-text"><span class="text-highlight">A partir de [00] de [mês]</span>, as regras de Perfil de Investidor serão ajustadas para deixar sua experiência mais personalizada.</span>
        <span class="content-text">Para evitar interrupções futuras, responda às perguntas agora, e seu perfil será atualizado na data da mudança.</span>
        <span class="text-highlight">Importante: com essas mudanças, somente usuários com perfil Agressivo poderão fazer operações Day Trade.</span>`
      }
    },
    PostAnticipation: {
      Varejo: {
        title: 'Novas regras do perfil de Investidor',
        message: `<span class="content-text"><span class="text-highlight">A partir de [00] de [mês]</span>, as regras de Perfil de Investidor serão ajustadas para deixar sua experiência
        mais personalizada.</span>
        Para evitar interrupções futuras, responda às perguntas agora, e seu perfil será
        atualizado na data da mudança.`
      },
      Trader: {
        title: 'Atualize seu perfil para continuar operando Day Trade',
        message: `<span class="content-text"><span class="text-highlight">A partir de [00] de [mês]</span>, as regras de Perfil de Investidor serão ajustadas para deixar sua experiência mais personalizada.</span>
        <span class="content-text">Para evitar interrupções futuras, responda às perguntas agora, e seu perfil será atualizado na data da mudança.</span>
        <span class="text-highlight">Importante: com essas mudanças, somente usuários com perfil Agressivo poderão fazer operações Day Trade.</span><br>`
      }
    }
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) private data,
    public elementRef: ElementRef,
    private router: Router,
    private dialogReference: MatDialogRef<SuitabilityAnticipationDialogComponent>,
    private store: Store<AppState>,
    private suitabilityService: SantanderSuitabilityService
  ) {
    super(elementRef, '.suitability-santander-anticipate-modal');
  }

  public ngOnInit(): void {
    this.isTrader = this.data.isTrader;
    this.isExclusive = this.data.isExclusive;
    const clientType = this.isTrader ? 'Trader' : 'Varejo';

    const releaseDate = this.suitabilityService.getReleaseDate();
    this.isPreAnticipation = this.suitabilityService.isInPreAnticipation();

    this.setDialogContent(releaseDate, clientType);
    this.sendModalOpenedMetrics();
  }

  public close(): void {
    this.sendCancelMetrics();
    super.close(this.dialogReference);
  }

  public updateInvestorProfile() {
    this.sendUpdateMetrics();

    this.router
      .navigate(['/novo-suitability/questionario'])
      .then(() => {
        super.close(this.dialogReference);
      });
  }

  public ngOnDestroy(): void { }

  private setDialogContent(releaseDate: Date, clientType: string) {
    const formattedDate = this.formatReleaseDate(releaseDate);

    this.content = this.isPreAnticipation ?
      this.messages.PreAnticipation[clientType] : this.messages.PostAnticipation[clientType];

    this.content.message = this.content.message
      .replace('[00]', formattedDate.split(' de ')[0])
      .replace('[mês]', formattedDate.split(' de ')[1]);
  }

  private sendMetrics(type, payload) {
    this.store.dispatch({
      type: TrackingActions.trackEvents, payload:
      {
        type: type,
        events: payload
      }
    });
  }

  private getEventProperties() {
    if (this.isExclusive) {
      return {
        'Product Area Origin': 'Home Exclusiva',
        'Status': 'Exclusivo',
        'Text': this.isPreAnticipation ? this.messages.PreAnticipation['Varejo'].title : this.messages.PostAnticipation['Varejo'].title
      };
    }

    if (this.isTrader) {
      return {
        'Product Area Origin': 'Home Trader',
        'Status': 'Trader',
        'Text': this.isPreAnticipation ? this.messages.PreAnticipation['Trader'].title : this.messages.PostAnticipation['Trader'].title
      };
    }

    return {
      'Product Area Origin': 'Home Investidor',
      'Status': 'Varejo',
      'Text': this.isPreAnticipation ? this.messages.PreAnticipation['Varejo'].title : this.messages.PostAnticipation['Varejo'].title
    };
  }

  private sendMetricsEvent(type: ActionEventsEnum, element: string, productAreaOrigin: string = null) {
    const clientTypeEvent = this.getEventProperties();

    let payload = {
      'Scenario': 'Pré-virada Suitability',
      'Time Responsável': 'Dumbo',
      ...clientTypeEvent,
    };

    payload['Product Area Origin'] = productAreaOrigin ? productAreaOrigin : payload['Product Area Origin'];

    if (type == ActionEventsEnum.ELEMENT_CLICKED) {
      payload = {
        ...payload,
        'Element': element,
        'Group': 'Modal',
      } as any;
    }

    this.sendMetrics(type, payload);
  }

  private sendModalOpenedMetrics() {
    this.sendMetricsEvent(ActionEventsEnum.MODAL_OPENED, '', this.data.productAreaOrigin);
  }

  private sendUpdateMetrics() {
    this.sendMetricsEvent(ActionEventsEnum.ELEMENT_CLICKED, 'Botão atualizar Perfil de Investidor',);
  }

  private sendCancelMetrics() {
    this.sendMetricsEvent(ActionEventsEnum.ELEMENT_CLICKED, 'Botão lembrar depois');
  }

  private formatReleaseDate(date: Date): string {
    const day = date.getDate();
    const month = date.toLocaleString('pt-BR', { month: 'long' });
    return `${day} de ${month}`;
  }

}
